import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileDown, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

// Preview (new tab) or download a PDF from an authenticated endpoint.
export const openPdf = async (path, filename) => {
  const win = filename ? null : window.open("", "_blank");  // opened inside the click so popup blockers allow it
  try {
    const { data } = await client.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    if (filename) {
      const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    } else if (win) { win.location.href = url; } else { window.location.href = url; }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { if (win) win.close(); toast.error("Could not build the PDF"); }
};

export const openChecklistPdf = (iid, filename, date) => openPdf(`/forms/instances/${iid}/report.pdf${date ? `?date=${date}` : ""}`, filename);

// Preview the client email for a completed checklist, then send it.
export const ChecklistEmailDialog = ({ iid, initialDate, onOpenChange, onSent }) => {
  const [p, setP] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState("");
  useEffect(() => {
    if (iid) {
      setP(null); setNote("");
      const d0 = initialDate || "";
      client.get(`/forms/instances/${iid}/email-preview${d0 ? `?date=${d0}` : ""}`).then((r) => { setP(r.data); setDate(d0 || r.data.jobDate || ""); })
        .catch((e) => { toast.error(apiErr(e.response?.data?.detail)); onOpenChange(false); });
    }
  }, [iid]); // eslint-disable-line react-hooks/exhaustive-deps
  const changeDate = (d) => {
    setDate(d);
    if (d) { client.get(`/forms/instances/${iid}/email-preview?date=${d}`).then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail))); }
  };
  const custom = date && p && date !== p.jobDate;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/forms/instances/${iid}/email`, { note, date: custom ? date : null });
      toast.success(`Emailed to ${data.to}`); onSent?.(); onOpenChange(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!iid} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="checklist-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-brand" />Email checklist to client</DialogTitle>
          <DialogDescription>Check the email below, then press Send. Wording comes from the email template on Forms & Checklists.</DialogDescription>
        </DialogHeader>
        {!p ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 divide-y text-sm">
              <div className="px-3 py-2 flex gap-2"><span className="w-16 text-slate-500">From</span><span className="font-medium" data-testid="email-preview-from">{p.from}</span></div>
              <div className="px-3 py-2 flex gap-2"><span className="w-16 text-slate-500">To</span>
                {p.to ? <span className="font-medium" data-testid="email-preview-to">{p.to}</span> : <span className="text-red-600" data-testid="email-preview-no-to">No email saved for this client — add one on the client's page</span>}</div>
              {p.copyTo && <div className="px-3 py-2 flex gap-2"><span className="w-16 text-slate-500">Copy</span><span className="text-slate-700" data-testid="email-preview-copy">{p.copyTo} <span className="text-xs text-slate-400">(hidden from the client)</span></span></div>}
              <div className="px-3 py-2 flex gap-2"><span className="w-16 text-slate-500">Subject</span><span className="font-medium" data-testid="email-preview-subject">{p.subject}</span></div>
              <div className="px-3 py-3 whitespace-pre-wrap text-slate-700" data-testid="email-preview-body">{p.body}
                {note.trim() && <div className="mt-3 border-l-4 border-brand bg-orange-50 px-3 py-2" data-testid="email-preview-note">{note}</div>}
                <div className="mt-3 text-xs text-slate-500">The checklist PDF and job photos are attached to this email.</div>
                <div className="mt-3"><span className="inline-block rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white">View checklist report (PDF)</span></div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <Label>Date shown on the PDF and email</Label>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Input type="date" value={date} onChange={(e) => changeDate(e.target.value)} className="h-10 w-48" data-testid="email-date" />
                {custom && <Button size="sm" variant="ghost" onClick={() => changeDate(p.jobDate)} data-testid="email-date-reset">Use job date</Button>}
              </div>
              <p className="mt-1.5 text-xs text-slate-500" data-testid="email-date-hint">{custom ? "Only this email and PDF use this date — the job stays on its scheduled day." : "This is the job's date. Change it here for this email only."}</p>
            </div>
            <div><Label>Add a note for this email only (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1.5" data-testid="email-note" /></div>
            {p.emailedAt && <p className="text-xs text-slate-500" data-testid="email-last-sent">Last emailed to {p.emailedTo} on {new Date(p.emailedAt).toLocaleString("en-GB")}</p>}
            <div className="flex flex-wrap justify-between gap-2">
              <Button variant="outline" onClick={() => openChecklistPdf(iid, null, custom ? date : null)} data-testid="email-preview-pdf"><FileDown className="h-4 w-4 mr-1" />Preview PDF</Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="email-cancel">Cancel</Button>
                <Button onClick={send} disabled={busy || !p.to || !p.submitted} className="bg-brand hover:bg-brand-dark" data-testid="email-send">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" />Send</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
